import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, num, euro, downloadFile } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table2, Layers, FileSpreadsheet, ChevronDown, Maximize2, Minimize2, Cable, CheckCircle2, FolderOpen } from "lucide-react";
import ImportPreviewDialog from "@/components/ImportPreviewDialog";
import ImportButton from "@/components/ImportButton";
import Combobox from "@/components/Combobox";
import { useTableSort } from "@/lib/useTableSort";
import { toast } from "sonner";
import { NEW_TRATTA, NEW_AREA_CAVO } from "./contabilita/constants";
import SottocommesseTable from "@/components/SottocommesseTable";
import { useContabilitaColumns } from "./contabilita/useContabilitaColumns";
import ContabilitaGrid from "./contabilita/ContabilitaGrid";
import ContabilitaManagePanels from "./contabilita/ContabilitaManagePanels";
import SubappaltoDialog from "./contabilita/SubappaltoDialog";
import { useAuth } from "@/context/AuthContext";

export default function Contabilita() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [commesse, setCommesse] = useState([]);
  const [riepilogo, setRiepilogo] = useState({});
  const [listino, setListino] = useState([]);
  const [squadre, setSquadre] = useState([]);
  const [competenze, setCompetenze] = useState([]);
  const [subappaltatori, setSubappaltatori] = useState([]);
  const [commessaId, setCommessaId] = useState("");
  const [contabilitaList, setContabilitaList] = useState([]);
  const [contabilitaId, setContabilitaId] = useState("");
  const [listini, setListini] = useState([]);
  const [tratte, setTratte] = useState([]);
  const [righe, setRighe] = useState([]);

  // draft new row
  const [draftTratta, setDraftTratta] = useState("");
  const [draftVoce, setDraftVoce] = useState("");
  const [draftQta, setDraftQta] = useState("");
  const [draftData, setDraftData] = useState(() => new Date().toISOString().slice(0, 10));
  const [draftSquadra, setDraftSquadra] = useState("");
  const [draftCompetenza, setDraftCompetenza] = useState("");
  const [draftAreaCavo, setDraftAreaCavo] = useState("");
  const [draftNote, setDraftNote] = useState("");
  const [newMode, setNewMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAreaCavoMode, setNewAreaCavoMode] = useState(false);
  const [newAreaCavoName, setNewAreaCavoName] = useState("");

  // subappalto dialog
  const [subRiga, setSubRiga] = useState(null);
  const [subQta, setSubQta] = useState("");
  const [subAzienda, setSubAzienda] = useState("");

  // pannello gestione tratte
  const [trattePanel, setTrattePanel] = useState(false);
  const [panelNewTratta, setPanelNewTratta] = useState("");
  const [editTrattaId, setEditTrattaId] = useState(null);
  const [areeCavo, setAreeCavo] = useState([]);
  const [areaCavoPanel, setAreaCavoPanel] = useState(false);
  const [panelNewAreaCavo, setPanelNewAreaCavo] = useState("");
  const [editAreaCavoId, setEditAreaCavoId] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);

  // pannello gestione contabilità
  const [coPanel, setCoPanel] = useState(false);
  const [coName, setCoName] = useState("");
  const [coEditId, setCoEditId] = useState(null);
  const [coTipo, setCoTipo] = useState("attiva");
  const [coSubId, setCoSubId] = useState("");

  useEffect(() => {
    api.get("/commesse").then((r) => setCommesse(r.data));
    api.get("/kpi/commesse-riepilogo").then((r) => setRiepilogo(r.data));
    api.get("/listini").then((r) => setListini(r.data));
    api.get("/squadre").then((r) => setSquadre(r.data));
    api.get("/competenze").then((r) => setCompetenze(r.data));
    api.get("/subappaltatori").then((r) => setSubappaltatori(r.data));
  }, []);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useEffect(() => {
    const cid = searchParams.get("commessa");
    if (cid) setCommessaId(cid);
  }, [searchParams]);

  const reloadContabilita = useCallback((cid) => api.get(`/commesse/${cid}/contabilita`).then((r) => { setContabilitaList(r.data); return r.data; }), []);
  const reloadTratte = useCallback((coid) => api.get("/tratte", { params: { contabilita_id: coid } }).then((r) => { setTratte(r.data); return r.data; }), []);
  const loadRighe = useCallback((coid) => {
    if (!coid) return setRighe([]);
    api.get("/righe", { params: { contabilita_id: coid } }).then((r) => setRighe(r.data));
  }, []);

  // Cambio commessa → carica le contabilità (creane una di default se assente) e seleziona la prima
  useEffect(() => {
    if (!commessaId) { setContabilitaList([]); setContabilitaId(""); setAreeCavo([]); return; }
    api.get("/aree_cavo", { params: { commessa_id: commessaId } }).then((r) => setAreeCavo(r.data));
    reloadContabilita(commessaId).then(async (list) => {
      if (list.length === 0) {
        const commessa = commesse.find((c) => c.id === commessaId);
        const { data } = await api.post(`/commesse/${commessaId}/contabilita`, { nome: "Contabilità principale", listini_ids: commessa?.listini_ids || [] });
        setContabilitaList([data]);
        setContabilitaId(data.id);
      } else {
        setContabilitaId(list[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commessaId, reloadContabilita]);

  // Cambio contabilità → carica tratte/righe/voci
  useEffect(() => {
    if (contabilitaId) {
      reloadTratte(contabilitaId);
      loadRighe(contabilitaId);
      api.get(`/contabilita/${contabilitaId}/voci`).then((r) => setListino(r.data));
    } else { setTratte([]); setRighe([]); setListino([]); }
    setDraftTratta(""); setDraftVoce(""); setDraftQta(""); setNewMode(false); setNewName("");
    setDraftSquadra(""); setDraftCompetenza(""); setDraftAreaCavo(""); setDraftNote("");
    setNewAreaCavoMode(false); setNewAreaCavoName("");
  }, [contabilitaId, reloadTratte, loadRighe]);

  const currentCo = contabilitaList.find((c) => c.id === contabilitaId) || null;
  const coTipoCurrent = currentCo?.tipo || "attiva";

  const trattaName = (id) => tratte.find((t) => t.id === id)?.nome || "—";

  const confirmNewTratta = async () => {
    if (!newName.trim()) return toast.error("Inserisci il nome della tratta");
    try {
      const { data } = await api.post("/tratte", { nome: newName, commessa_id: commessaId, contabilita_id: contabilitaId });
      await reloadTratte(contabilitaId);
      setDraftTratta(data.id);
      setNewMode(false);
      setNewName("");
      toast.success("Tratta creata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onDraftTrattaChange = (v) => {
    if (v === NEW_TRATTA) { setNewMode(true); setNewName(""); }
    else setDraftTratta(v);
  };

  const confirmNewAreaCavo = async () => {
    if (!newAreaCavoName.trim()) return toast.error("Inserisci il nome dell'area");
    try {
      const { data } = await api.post("/aree_cavo", { nome: newAreaCavoName.trim(), commessa_id: commessaId });
      await reloadAreeCavo();
      setDraftAreaCavo(data.nome);
      setNewAreaCavoMode(false);
      setNewAreaCavoName("");
      toast.success("Area creata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onDraftAreaCavoChange = (v) => {
    if (v === NEW_AREA_CAVO) { setNewAreaCavoMode(true); setNewAreaCavoName(""); }
    else setDraftAreaCavo(v);
  };

  const addTrattaFromPanel = async () => {
    if (!panelNewTratta.trim()) return toast.error("Inserisci il nome della tratta");
    try {
      await api.post("/tratte", { nome: panelNewTratta, commessa_id: commessaId, contabilita_id: contabilitaId });
      await reloadTratte(contabilitaId);
      setPanelNewTratta("");
      toast.success("Tratta creata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reloadAreeCavo = useCallback(() => api.get("/aree_cavo", { params: { commessa_id: commessaId } }).then((r) => { setAreeCavo(r.data); return r.data; }), [commessaId]);
  const addAreaCavoFromPanel = async () => {
    if (!panelNewAreaCavo.trim()) return toast.error("Inserisci il nome dell'area");
    try {
      await api.post("/aree_cavo", { nome: panelNewAreaCavo.trim(), commessa_id: commessaId });
      await reloadAreeCavo();
      setPanelNewAreaCavo("");
      toast.success("Area creata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const renameAreaCavo = async (a, nuovo) => {
    const nome = (nuovo || "").trim();
    setEditAreaCavoId(null);
    if (!nome || nome === a.nome) return;
    try {
      await api.put(`/aree_cavo/${a.id}`, { nome });
      await reloadAreeCavo();
      loadRighe(contabilitaId);
      toast.success("Area rinominata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const removeAreaCavo = async (id) => {
    await api.delete(`/aree_cavo/${id}`);
    await reloadAreeCavo();
    toast.success("Area eliminata");
  };

  const exportContab = (variante) => {
    const cod = commesse.find((c) => c.id === commessaId)?.codice || "commessa";
    downloadFile(`/commesse/${commessaId}/export/excel`, `contabilita_${cod}_${variante}.xlsx`, { variante, contabilita_id: contabilitaId })
      .catch(() => toast.error("Errore durante l'esportazione"));
  };

  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/commesse/${commessaId}/righe/import/preview`, fd, { params: { contabilita_id: contabilitaId } });
      setPreviewData(data);
      setPendingFile(file);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anteprima non riuscita");
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const { data } = await api.post(`/commesse/${commessaId}/righe/import`, fd, { params: { contabilita_id: contabilitaId } });
      await Promise.all([reloadTratte(contabilitaId), loadRighe(contabilitaId)]);
      const skip = data.skipped?.length || 0;
      const upd = data.updated || 0;
      const parts = [`${data.inserted} righe importate`];
      if (upd) parts.push(`${upd} integrate`);
      if (skip) parts.push(`${skip} scartate`);
      toast.success(parts.join(" · "));
      setPreviewOpen(false);
      setPendingFile(null);
      setPreviewData(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import non riuscito");
    } finally {
      setImporting(false);
    }
  };

  const addRiga = async () => {
    if (!draftTratta) return toast.error("Seleziona o crea una tratta");
    if (!draftVoce) return toast.error("Seleziona una voce di capitolato");
    const voce = listino.find((v) => v.id === draftVoce);
    try {
      await api.post("/righe", {
        tratta_id: draftTratta,
        commessa_id: commessaId,
        contabilita_id: contabilitaId,
        voce_id: voce.id,
        codice: voce.codice,
        descrizione: voce.descrizione,
        unita_misura: voce.unita_misura,
        prezzo_unitario: voce.prezzo_unitario,
        quantita: parseFloat(draftQta) || 0,
        data: draftData || undefined,
        squadra: draftSquadra,
        competenza: draftCompetenza,
        area_cavo: draftAreaCavo || "",
        note: draftNote || "",
      });
      setDraftVoce("");
      setDraftQta("");
      setDraftAreaCavo("");
      setDraftNote("");
      loadRighe(contabilitaId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const savedTimer = useRef(null);
  const [showSaved, setShowSaved] = useState(false);
  const flashSaved = () => {
    setShowSaved(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setShowSaved(false), 2500);
  };

  const putRiga = async (riga, patch) => {
    const m = { ...riga, ...patch };
    setRighe((prev) => prev.map((r) => (r.id === riga.id
      ? { ...m, importo: (m.quantita || 0) * (m.prezzo_unitario || 0), importo_sub: (m.quantita_subappalto || 0) * (m.prezzo_sub || 0) }
      : r)));
    await api.put(`/righe/${riga.id}`, {
      tratta_id: m.tratta_id, commessa_id: m.commessa_id, contabilita_id: m.contabilita_id, voce_id: m.voce_id,
      codice: m.codice, descrizione: m.descrizione, unita_misura: m.unita_misura,
      prezzo_unitario: m.prezzo_unitario, quantita: m.quantita, data: m.data, note: m.note, area_cavo: m.area_cavo || "",
      squadra: m.squadra || "", competenza: m.competenza || "", pagato: !!m.pagato,
      quantita_subappalto: m.quantita_subappalto || 0, voce_sub_id: m.voce_sub_id || null,
      codice_sub: m.codice_sub || "", descrizione_sub: m.descrizione_sub || "", prezzo_sub: m.prezzo_sub || 0,
      subappaltatore: m.subappaltatore || "", subappaltatore_id: m.subappaltatore_id || null,
    });
    flashSaved();
    if (patch && Object.prototype.hasOwnProperty.call(patch, "pagato")) {
      api.get("/kpi/commesse-riepilogo").then((r) => setRiepilogo(r.data)).catch(() => {});
    }
  };

  const updateQta = (riga, value) => {
    const qta = parseFloat(value);
    if (isNaN(qta)) return;
    putRiga(riga, { quantita: qta });
  };

  const changeTratta = (riga, v) => {
    if (v === NEW_TRATTA) return;
    putRiga(riga, { tratta_id: v });
  };

  const removeRiga = async (id) => {
    await api.delete(`/righe/${id}`);
    loadRighe(contabilitaId);
  };

  const removeTratta = async (id) => {
    await api.delete(`/tratte/${id}`);
    toast.success("Tratta eliminata");
    reloadTratte(contabilitaId);
    loadRighe(contabilitaId);
  };

  const renameTratta = async (t, nome) => {
    const nuovo = (nome || "").trim();
    if (!nuovo || nuovo === t.nome) { setEditTrattaId(null); return; }
    try {
      await api.put(`/tratte/${t.id}`, { nome: nuovo, commessa_id: commessaId, contabilita_id: contabilitaId });
      setEditTrattaId(null);
      await reloadTratte(contabilitaId);
      loadRighe(contabilitaId);
      toast.success("Tratta rinominata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const createContabilita = async () => {
    const nome = coName.trim();
    if (!nome) return toast.error("Inserisci il nome della contabilità");
    if (coTipo === "subappalto" && !coSubId) return toast.error("Seleziona il subappaltatore");
    try {
      const azienda = subappaltatori.find((s) => s.id === coSubId);
      const { data } = await api.post(`/commesse/${commessaId}/contabilita`, {
        nome, tipo: coTipo, listini_ids: [],
        subappaltatore_id: coTipo === "subappalto" ? coSubId : null,
        subappaltatore: coTipo === "subappalto" ? (azienda?.nome || "") : "",
      });
      setCoName(""); setCoTipo("attiva"); setCoSubId("");
      await reloadContabilita(commessaId);
      setContabilitaId(data.id);
      toast.success("Contabilità creata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const renameContabilita = async (co, nome) => {
    const n = (nome || "").trim();
    if (!n || n === co.nome) { setCoEditId(null); return; }
    try {
      await api.put(`/contabilita/${co.id}`, { nome: n, tipo: co.tipo, subappaltatore_id: co.subappaltatore_id, subappaltatore: co.subappaltatore, listini_ids: co.listini_ids || [] });
      setCoEditId(null);
      await reloadContabilita(commessaId);
      toast.success("Contabilità rinominata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeContabilita = async (co) => {
    try {
      await api.delete(`/contabilita/${co.id}`);
      const list = await reloadContabilita(commessaId);
      if (co.id === contabilitaId) setContabilitaId(list[0]?.id || "");
      toast.success("Contabilità eliminata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setCoListini = async (co, listini_ids) => {
    try {
      await api.put(`/contabilita/${co.id}`, { nome: co.nome, tipo: co.tipo, subappaltatore_id: co.subappaltatore_id, subappaltatore: co.subappaltatore, listini_ids });
      await reloadContabilita(commessaId);
      if (co.id === contabilitaId) {
        api.get(`/contabilita/${contabilitaId}/voci`).then((r) => setListino(r.data));
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Navigazione tipo Excel gestita dalla griglia (frecce/Invio/Tab).
  const onDraftQtaKey = (e) => {
    if (e.key === "Enter") addRiga();
  };

  const openSub = (r) => {
    setSubRiga(r);
    setSubQta(r.quantita_subappalto ? String(r.quantita_subappalto) : "");
    setSubAzienda(r.subappaltatore_id || "");
  };

  const saveSub = async () => {
    const qta = parseFloat(subQta) || 0;
    if (qta > (subRiga.quantita || 0)) return toast.error("La quantità subappalto non può superare la quantità della riga");
    if (qta > 0 && !subAzienda) return toast.error("Seleziona il subappaltatore");
    const azienda = subappaltatori.find((s) => s.id === subAzienda);
    // Il prezzo/importo subappalto e la riga specchio nella contabilità di
    // subappalto vengono calcolati/creati lato server (confluenza automatica).
    await putRiga(subRiga, {
      quantita_subappalto: qta,
      subappaltatore_id: azienda ? azienda.id : null,
      subappaltatore: azienda ? azienda.nome : "",
    });
    setSubRiga(null);
    await Promise.all([reloadContabilita(commessaId), loadRighe(contabilitaId)]);
    toast.success("Subappalto aggiornato");
  };

  // Ordine predefinito: per data di inserimento crescente (nuove in fondo).
  const sortedRighe = [...righe].sort((x, y) => (x.created_at || "").localeCompare(y.created_at || ""));

  // ---------- Filtri colonne ----------
  const emptyFilters = { tratta: [], mese: [], squadra: [], competenza: [], codice: "", voce: "", um: "", prezzo: "", quantita: "", importo: "", sub: "", areaCavo: [], pagato: [] };
  const [filters, setFilters] = useState(emptyFilters);
  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  useEffect(() => { setFilters(emptyFilters); }, [commessaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const mesiDisponibili = [...new Set(righe.map((r) => (r.data || "").slice(0, 7)).filter(Boolean))].sort().reverse();
  const meseLabel = (m) => {
    const [y, mm] = m.split("-");
    const nome = new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    return nome.charAt(0).toUpperCase() + nome.slice(1);
  };
  const geq = (val, f) => f === "" || (Number(val) || 0) >= (parseFloat(f) || 0);
  const has = (val, f) => f === "" || String(val || "").toLowerCase().includes(f.toLowerCase());
  const inSel = (arr, v) => arr.length === 0 || arr.includes(v);

  const filteredRighe = sortedRighe.filter((r) =>
    inSel(filters.tratta, r.tratta_id) &&
    inSel(filters.mese, (r.data || "").slice(0, 7)) &&
    inSel(filters.squadra, r.squadra) &&
    inSel(filters.competenza, r.competenza) &&
    has(r.codice, filters.codice) && has(r.descrizione, filters.voce) && has(r.unita_misura, filters.um) &&
    geq(r.prezzo_unitario, filters.prezzo) && geq(r.quantita, filters.quantita) &&
    geq(r.importo, filters.importo) && geq(r.importo_sub, filters.sub) &&
    inSel(filters.areaCavo, r.area_cavo) &&
    inSel(filters.pagato, r.pagato ? "si" : "no")
  );
  const hasActiveFilter = Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : v !== ""));

  const sortAccessors = {
    tratta: (r) => trattaName(r.tratta_id),
    data: (r) => r.data || "",
    squadra: (r) => r.squadra || "",
    competenza: (r) => r.competenza || "",
    codice: (r) => r.codice || "",
    descrizione: (r) => r.descrizione || "",
    um: (r) => r.unita_misura || "",
    prezzo: (r) => r.prezzo_unitario || 0,
    quantita: (r) => r.quantita || 0,
    importo: (r) => r.importo || 0,
    pagato: (r) => (r.pagato ? 1 : 0),
    sub: (r) => r.importo_sub || 0,
    inserito: (r) => r.created_by || "",
    inseritoIl: (r) => r.created_at || "",
    note: (r) => r.note || "",
    areaCavo: (r) => r.area_cavo || "",
  };
  const { sorted: displayRighe, sortKey, sortDir, toggleSort } = useTableSort(filteredRighe, sortAccessors);

  const totale = filteredRighe.reduce((s, r) => s + (r.importo || 0), 0);
  const totaleSub = filteredRighe.reduce((s, r) => s + (r.importo_sub || 0), 0);
  const draftVoceObj = listino.find((v) => v.id === draftVoce);

  const allColumns = useContabilitaColumns({
    tratte, listino, squadre, competenze, areeCavo,
    filters, setF, mesiDisponibili, meseLabel,
    changeTratta, putRiga, updateQta, openSub, removeRiga,
    draftTratta, onDraftTrattaChange, newMode, newName, setNewName, confirmNewTratta, setNewMode,
    draftData, setDraftData, draftSquadra, setDraftSquadra, draftCompetenza, setDraftCompetenza,
    draftVoce, setDraftVoce, draftQta, setDraftQta, onDraftQtaKey, draftVoceObj, addRiga,
    draftAreaCavo, setDraftAreaCavo, draftNote, setDraftNote,
    newAreaCavoMode, setNewAreaCavoMode, newAreaCavoName, setNewAreaCavoName, confirmNewAreaCavo, onDraftAreaCavoChange,
    totale, totaleSub, hasActiveFilter, onClearFilters: () => setFilters(emptyFilters),
    totaleRiconosciuto: riepilogo[commessaId]?.riconosciuto || 0,
    canEditPagato: true,
  });
  const befMode = commesse.find((c) => c.id === commessaId)?.bef_mode || "fatture";
  // La colonna subappalto ha senso solo nelle contabilità attive;
  // la colonna "Pagato" solo per le commesse in modalità riconoscimento "pagato".
  const columns = (coTipoCurrent === "attiva" ? allColumns : allColumns.filter((c) => c.id !== "sub"))
    .filter((c) => c.id !== "pagato" || befMode === "pagato");
  const isParentView = !!commessaId && commesse.some((c) => c.parent_id === commessaId);

  return (
    <>
      <ImportPreviewDialog
        open={previewOpen} onOpenChange={setPreviewOpen}
        title="Anteprima import contabilità" preview={previewData}
        onConfirm={confirmImport} importing={importing}
        columns={[
          { key: "tratta", label: "Tratta" },
          { key: "codice", label: "VDC" },
          { key: "descrizione", label: "Descrizione" },
          { key: "unita_misura", label: "UM" },
          { key: "quantita", label: "Q.tà", right: true },
          { key: "prezzo_unitario", label: "Prezzo €", right: true, format: (v) => num(v) },
          { key: "importo", label: "Importo €", right: true, format: (v) => num(v) },
          { key: "data", label: "Data" },
          { key: "squadra", label: "Squadra" },
          { key: "competenza", label: "Competenza" },
          { key: "area_cavo", label: "Area" },
        ]}
      />

      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-5">
        {showSaved && (
          <div data-testid="saved-indicator" className="fixed top-4 right-6 z-50 flex items-center gap-2 rounded-full bg-emerald-600 text-white text-sm font-medium px-4 py-2 shadow-lg animate-in fade-in slide-in-from-top-2 duration-200">
            <CheckCircle2 className="h-4 w-4" /> Tutte le modifiche sono state salvate
          </div>
        )}
        <PageHeader title="Contabilità" actions={commessaId && (
          <>
            <input ref={importInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} className="hidden" data-testid="import-righe-input" />
            <ImportButton testid="import-righe" importing={importing}
              onPickFile={() => importInputRef.current?.click()}
              onTemplate={() => downloadFile(`/commesse/${commessaId}/righe/template`, "template_contabilita.xlsx").catch(() => toast.error("Errore download template"))} />
            <Button variant="outline" size="sm" data-testid="export-cliente-button" onClick={() => exportContab("cliente")}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export per cliente
            </Button>
            <Button variant="outline" size="sm" data-testid="export-interno-button" onClick={() => exportContab("interno")}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export interno
            </Button>
          </>
        )} />
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-[440px] max-w-full">
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Commessa</Label>
            <div className="mt-1.5">
              <Combobox testid="select-commessa" value={commessaId} onChange={setCommessaId}
                placeholder="Seleziona commessa" triggerClassName="h-10" wrap contentClassName="min-w-[560px]"
                options={(() => {
                  const parents = commesse.filter((c) => !c.parent_id);
                  const childrenOf = (pid) => commesse.filter((c) => c.parent_id === pid);
                  const fmt = (c) => `${c.nome} / ${c.codice}${[c.id_cliente, c.comune, c.indirizzo].map((x) => ` - ${x || "—"}`).join("")}`;
                  const opts = [];
                  parents.forEach((p) => {
                    const kids = childrenOf(p.id);
                    if (kids.length) {
                      opts.push({ value: p.id, label: `${p.codice} · ${p.nome} — riepilogo sottocommesse` });
                      kids.forEach((k) => {
                        opts.push({ value: k.id, label: fmt(k), className: "pl-9" });
                      });
                    } else opts.push({ value: p.id, label: fmt(p) });
                  });
                  return opts;
                })()} />
            </div>
          </div>
          {commessaId && !isParentView && (
            <div className="w-72">
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Contabilità</Label>
              <div className="mt-1.5">
                <Combobox testid="select-contabilita" value={contabilitaId} onChange={setContabilitaId}
                  placeholder="Seleziona contabilità" triggerClassName="h-10"
                  options={contabilitaList.map((co) => ({ value: co.id, label: co.nome }))} />
              </div>
            </div>
          )}
          {commessaId && commesse.find((c) => c.id === commessaId)?.parent_id && (
            <Button variant="outline" data-testid="apri-scheda-sottocommessa" onClick={() => navigate(`/commesse/${commessaId}`)} className="h-10 gap-2 bg-white">
              <FolderOpen className="h-4 w-4 text-blue-700" /> Scheda sottocommessa
            </Button>
          )}
          {commessaId && !isParentView && isAdmin && (
            <Button variant="outline" data-testid="gestisci-contabilita-button" onClick={() => setCoPanel((o) => !o)} className="h-10 gap-2 bg-white">
              <Table2 className="h-4 w-4 text-blue-700" /> Gestisci contabilità
              <span className="text-xs font-semibold text-blue-700 bg-blue-50 rounded-full px-1.5">{contabilitaList.length}</span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${coPanel ? "rotate-180" : ""}`} />
            </Button>
          )}
          {commessaId && !isParentView && (
            <Button variant="outline" data-testid="gestisci-tratte-button" onClick={() => setTrattePanel((o) => !o)} className="h-10 gap-2 bg-white">
              <Layers className="h-4 w-4 text-blue-700" /> Gestisci tratte
              <span className="text-xs font-semibold text-blue-700 bg-blue-50 rounded-full px-1.5">{tratte.length}</span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${trattePanel ? "rotate-180" : ""}`} />
            </Button>
          )}
          {commessaId && !isParentView && (
            <Button variant="outline" data-testid="gestisci-aree-cavo-button" onClick={() => setAreaCavoPanel((o) => !o)} className="h-10 gap-2 bg-white">
              <Cable className="h-4 w-4 text-blue-700" /> Gestisci Aree
              <span className="text-xs font-semibold text-blue-700 bg-blue-50 rounded-full px-1.5">{areeCavo.length}</span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${areaCavoPanel ? "rotate-180" : ""}`} />
            </Button>
          )}
        </div>

        {!commessaId ? (
          <EmptyState icon={Table2} title="Seleziona una commessa" text="Scegli una commessa per gestirne la contabilità." />
        ) : commesse.filter((c) => c.parent_id === commessaId).length ? (
          <div className="bg-white rounded-md border border-slate-200 shadow-sm p-5" data-testid="sottocommesse-riepilogo">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-heading font-semibold text-slate-900 mb-1">Sottocommesse</h3>
                <p className="text-sm text-slate-400">Riepilogo della contabilità per sottocommessa. Clicca una voce per aprirne le righe.</p>
              </div>
              <Button variant="outline" data-testid="export-riepilogo" className="h-9 gap-2 bg-white flex-shrink-0"
                onClick={() => downloadFile("/kpi/riepilogo-export", `riepilogo_${commesse.find((c) => c.id === commessaId)?.codice || "commessa"}.xlsx`, { commessa_id: commessaId })}>
                <FileSpreadsheet className="h-4 w-4 text-emerald-700" /> Esporta riepilogo
              </Button>
            </div>
            <SottocommesseTable
              subs={commesse.filter((c) => c.parent_id === commessaId)}
              mode="contabilita"
              riepilogo={riepilogo}
              onOpen={(s) => setCommessaId(s.id)}
            />
          </div>
        ) : (
          <>
            <ContabilitaManagePanels
              coPanel={coPanel} setCoPanel={setCoPanel}
              contabilitaList={contabilitaList} contabilitaId={contabilitaId} setContabilitaId={setContabilitaId}
              coName={coName} setCoName={setCoName} createContabilita={createContabilita}
              coTipo={coTipo} setCoTipo={setCoTipo} coSubId={coSubId} setCoSubId={setCoSubId} subappaltatori={subappaltatori}
              coEditId={coEditId} setCoEditId={setCoEditId} renameContabilita={renameContabilita}
              removeContabilita={removeContabilita} listini={listini} setCoListini={setCoListini}
              trattePanel={trattePanel} setTrattePanel={setTrattePanel} tratte={tratte}
              panelNewTratta={panelNewTratta} setPanelNewTratta={setPanelNewTratta} addTrattaFromPanel={addTrattaFromPanel}
              editTrattaId={editTrattaId} setEditTrattaId={setEditTrattaId} renameTratta={renameTratta} removeTratta={removeTratta}
              areaCavoPanel={areaCavoPanel} setAreaCavoPanel={setAreaCavoPanel} areeCavo={areeCavo}
              panelNewAreaCavo={panelNewAreaCavo} setPanelNewAreaCavo={setPanelNewAreaCavo} addAreaCavoFromPanel={addAreaCavoFromPanel}
              editAreaCavoId={editAreaCavoId} setEditAreaCavoId={setEditAreaCavoId} renameAreaCavo={renameAreaCavo} removeAreaCavo={removeAreaCavo}
              isAdmin={isAdmin}
            />

            <div className={`bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden ${fullscreen ? "fixed inset-0 z-50 rounded-none flex flex-col" : ""}`}>
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Righe contabilità</span>
                <button
                  data-testid="contabilita-fullscreen-toggle"
                  onClick={() => setFullscreen((f) => !f)}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium text-slate-600 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                  title={fullscreen ? "Riduci" : "Schermo intero"}
                >
                  {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                  {fullscreen ? "Riduci" : "Schermo intero"}
                </button>
              </div>
              <ContabilitaGrid
                columns={columns} rows={displayRighe}
                sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}
                scrollKey={contabilitaId}
                containerClassName={fullscreen ? "flex-1" : "max-h-[calc(100vh-360px)]"}
              />
            </div>
          </>
        )}
      </div>

      <SubappaltoDialog
        subRiga={subRiga} setSubRiga={setSubRiga} subQta={subQta} setSubQta={setSubQta}
        subAzienda={subAzienda} setSubAzienda={setSubAzienda}
        subappaltatori={subappaltatori} commessaId={commessaId} saveSub={saveSub}
      />
    </>
  );
}

const EmptyState = ({ icon: Icon, title, text }) => (
  <div className="bg-white rounded-md border border-slate-200 shadow-sm py-20 flex flex-col items-center text-center">
    <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center mb-4">
      <Icon className="h-6 w-6 text-slate-400" />
    </div>
    <p className="text-slate-600 font-medium">{title}</p>
    <p className="text-sm text-slate-400 mt-1 max-w-xs">{text}</p>
  </div>
);
